import Controllers from "@/controllers/index";
import ResultSetFactory from "@/factories/wind/diagnostic/ResultSetFactory";
import ObjectHandleService from "@/services/common/ObjectHandleService";

/**
 * Solicita os resultados dos diagnósticos.
 * @param params parâmetros para requisição http.
 * - monitoringElementId id do elemento de monitoração ad turbina.
 * - startDateTime Data de inicio da pesquisa.
 * - endDateTime Data final da pesquisa.
 * @returns Objeto com Arrays de Resultados de diagnósticos no período selecionado,
 *  com filtros para modos de falha.
 */
const GetResultSets = async(params = ResultSetFactory.ResultSetModel()) =>
{
	const response = await Controllers.WindDiagnosticResultSets.GetWindDiagnosticResultSets(params);

	// Esse throw acontece devido a um comportamento estranho que foi mantido no front da matriz.
	if (response.status !== 200) throw new Error(response.status);

	const apiResponse = response.data;

	const filteredResultSets = ResultSetFactory.FilteredResultSetView(apiResponse);

	filteredResultSets.mainResultSetsList = apiResponse.mainResultSetsList
		.map((mr) => ResultSetFactory.ResultSetView(mr));

	const { mainResultSetsList } = filteredResultSets;

	filteredResultSets.stage2ResultSets = GetResultSetFailureModeList(
		apiResponse.gearboxStage2ResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.stage3ResultSets = GetResultSetFailureModeList(
		apiResponse.gearboxStage3ResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.planetaryResultSets = GetResultSetFailureModeList(
		apiResponse.planetaryResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.angularMisalignmentHssResultSets = GetResultSetFailureModeList(
		apiResponse.angularMisalignmentHssResultSetsList,
		mainResultSetsList
	);
	filteredResultSets.parallelMisalignmentHssResultSets = GetResultSetFailureModeList(
		apiResponse.parallelMisalignmentHssResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.bearingsResultSets = GetResultSetFailureModeList(
		apiResponse.bearingsResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.loosenessResultSets = GetResultSetFailureModeList(
		apiResponse.loosenessResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.imbalanceMainShaftResultSets = GetResultSetFailureModeList(
		apiResponse.imbalanceMainShaftResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.parallelMisalignmentResultSets = GetResultSetFailureModeList(
		apiResponse.parallelMisalignmentResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.angularMisalignmentResultSets = GetResultSetFailureModeList(
		apiResponse.angularMisalignmentResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.generatorElectricFail = GetResultSetFailureModeList(
		apiResponse.electricFailureGeneratorResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.generatorShaftUnbalanceResultSets = GetResultSetFailureModeList(
		apiResponse.unbalanceGeneratorShaftResultSetsList,
		mainResultSetsList
	);

	filteredResultSets.parallelMisalignmentGeneratorShaftResultSets = GetResultSetFailureModeList(
		apiResponse.parallelMisalignmentGeneratorShaftResultSetsList,
		mainResultSetsList
	);

	return filteredResultSets;
};

/**
 * Solicita os piores resultados de cada diagnóstico diagnósticos.
 * @param params parâmetros para requisição http.
 * - monitoringElementId id do elemento de monitoração ad turbina.
 * - startDateTime Data de inicio da pesquisa.
 * - endDateTime Data final da pesquisa.
 * @returns Objeto com Arrays de Resultados de diagnósticos no período selecionado,
 *  com filtros para modos de falha.
 */
const GetWorstResultSets = async(params = ResultSetFactory.ResultSetModel()) =>
{
	const response = await Controllers.WindDiagnosticResultSets.GetWindDiagnosticWorstResultSets(params);

	// Esse throw acontece devido a um comportamento estranho que foi mantido no front da matriz.
	if (response.status !== 200) throw new Error(response.status);

	const apiResponse = response.data;

	const worstResultSets = ResultSetFactory.WorstResultSetView(apiResponse);

	worstResultSets.mainResultSetsList = apiResponse.mainResultSetsList
		.map((mr) => ResultSetFactory.ResultSetView(mr));

	const { mainResultSetsList } = worstResultSets;

	worstResultSets.stage2ResultSets = GetResultSetFailureModeList(
		apiResponse.gearboxStage2ResultSetsList, mainResultSetsList
	);

	worstResultSets.stage3ResultSets = GetResultSetFailureModeList(
		apiResponse.gearboxStage3ResultSetsList, mainResultSetsList
	);

	worstResultSets.planetaryResultSets = GetResultSetFailureModeList(
		apiResponse.planetaryResultSetsList, mainResultSetsList
	);

	worstResultSets.parallelMisalignmentGeneratorShaftResultSets = GetResultSetFailureModeList(
		apiResponse.parallelMisalignmentGeneratorShaftResultSetsList, mainResultSetsList
	);

	worstResultSets.angularMisalignmentHssResultSets = GetResultSetFailureModeList(
		apiResponse.angularMisalignmentHssResultSetsList, mainResultSetsList
	);
	worstResultSets.parallelMisalignmentHssResultSets = GetResultSetFailureModeList(
		apiResponse.parallelMisalignmentHssResultSetsList, mainResultSetsList
	);

	worstResultSets.generatorUpWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.generatorUpWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.generatorDownWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.generatorDownWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxHSSUpWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxHSSUpWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxHSSDownWindUpBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxHSSDownWindUpBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxHSSDownWindDownBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxHSSDownWindDownBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxIMSUpWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxIMSUpWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxIMSDownWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxIMSDownWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxLSSUpWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxLSSUpWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxLSSDownWindUpBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxLSSDownWindUpBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxLSSDownWindDownBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxLSSDownWindDownBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxPlanetaryBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxPlanetaryBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxPlanetaryDownWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxPlanetaryDownWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.gearboxPlanetaryUpWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.gearboxPlanetaryUpWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.mainShaftBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.mainShaftBearingResultSets, mainResultSetsList
	);

	worstResultSets.mainShaftUpWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.mainShaftUpWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.mainShaftDownWindBearingResultSets = GetResultSetFailureModeList(
		apiResponse.worstBearingsResults.mainShaftDownWindBearingResultSets, mainResultSetsList
	);

	worstResultSets.loosenessResultSets = GetResultSetFailureModeList(
		apiResponse.loosenessResultSetsList, mainResultSetsList
	);

	worstResultSets.imbalanceMainShaftResultSets = GetResultSetFailureModeList(
		apiResponse.imbalanceMainShaftResultSetsList, mainResultSetsList
	);

	worstResultSets.parallelMisalignmentResultSets = GetResultSetFailureModeList(
		apiResponse.parallelMisalignmentResultSetsList, mainResultSetsList
	);

	worstResultSets.angularMisalignmentResultSets = GetResultSetFailureModeList(
		apiResponse.angularMisalignmentResultSetsList, mainResultSetsList
	);

	worstResultSets.generatorShaftUnbalanceResultSets = GetResultSetFailureModeList(
		apiResponse.unbalanceGeneratorShaftResultSetsList, mainResultSetsList
	);

	worstResultSets.generatorElectricFail = GetResultSetFailureModeList(
		apiResponse.electricFailureGeneratorResultSetsList, mainResultSetsList
	);
	return worstResultSets;
};

/**
 * Adiciona as propriedades de potência/velocidade do vento/RPM nos result sets de cada modo de falha.
 * @param {Array} failureModeResultSetList Array de resultados de um modo de falha específico.
 * @param {Array} mainResultSetList Array de resultados de modo de falhas geral, já mapeados com atributos de
 * potência/velocidade do vento/RPM.
 * @returns {Array} Array de resultados de um modo de falha específico com potência/velocidade do vento/RPM.
 */
const GetResultSetFailureModeList = (failureModeResultSetList, mainResultSetList) =>
{
	if (!!failureModeResultSetList && failureModeResultSetList.length > 0)
	{
		return failureModeResultSetList
			.map((fmrs) => SetMainResultSetPropertiesOnResultSetFailureMode(mainResultSetList, fmrs))
			.filter(Boolean);
	}
	return new Array(0);
};

/**
 * Obtém potência/velocidade do vento do resultSetPrincipal e
 * adiciona nos resultados específicos de modo de falha as propriedades de potência/velocidade do vento.
 * @param {object} mainResultSetsList Array de resultados de modo de falhas geral, já mapeados com atributos de
 * potência/velocidade do vento/RPM.
 * @param {object} resultSetFailureMode Resultado de um modo de falha especifico que sofrerá a modificação.
 * @returns Resultado de um modo de falha especifico modificado.
 */
const SetMainResultSetPropertiesOnResultSetFailureMode = function(mainResultSetsList, resultSetFailureMode)
{
	const mainResultSet = mainResultSetsList
		.find(mrs => mrs.newResultSet.id === resultSetFailureMode.id);

	if (!ObjectHandleService.IsObject(mainResultSet) || ObjectHandleService.IsEmpty(mainResultSet)) return;

	const resultSetFailureModeModified = ResultSetFactory.ResultSetView(resultSetFailureMode);

	resultSetFailureModeModified.id = resultSetFailureMode.id;
	resultSetFailureModeModified.signalRegisterId = resultSetFailureMode.signalRegisterId;

	// Necessário devido a lógicas estranhas no backend com o dateTime.
	resultSetFailureModeModified.dateTime = mainResultSet.dateTime;
	resultSetFailureModeModified.power = mainResultSet.power;
	resultSetFailureModeModified.powerUnit = mainResultSet.powerUnit;
	resultSetFailureModeModified.windSpeed = mainResultSet.windSpeed;
	resultSetFailureModeModified.windSpeedUnit = mainResultSet.windSpeedUnit;
	resultSetFailureModeModified.phaseReference = mainResultSet.phaseReference;
	resultSetFailureModeModified.phaseReferenceUnit = mainResultSet.phaseReferenceUnit;
	return resultSetFailureModeModified;
};

export default { GetResultSets, GetWorstResultSets };